import { make } from 'vuex-pathify'
import apolloProvider from '@/services/apolloProvider.service'
import gql from 'graphql-tag'
import { format, addDays } from 'date-fns'

const state = {
  unhandledDelays: [],
  unhandledTrainDeviations: [],
  unhandledCrewDeviations: [],
  selectedDeviation: null,
  selectedTrainDeviation: null
}

const mutations = make.mutations(state)

const period = {
  from: format(addDays(new Date(), -1), 'yyyy-MM-dd'),
  to: format(new Date(), 'yyyy-MM-dd')
}

export const queryUnhandledDelays = async filter => {
  try {
    const { data } = await apolloProvider.clients.defaultClient.query({
      query: gql`
        query unhandledDelays($filter: UnhandledDelaysInput!) {
          unhandledDelays(filter: $filter) {
            id
            departure_date
            train_number
            location_place
            location_time
            advertised_location_time
            delay_minutes
            level1_code
            level2_code
            level3_code
            delay_place
            delay_reason_description
            responsible
            thr
            odoc
            comment
            delay {
              uuid
              event {
                title
                uuid
              }
            }
          }
        }
      `,
      fetchPolicy: 'no-cache',
      variables: {
        filter
      }
    })

    return data.unhandledDelays
  } catch (err) {
    console.log(err)
    return []
  }
}

export const queryUnhandledCrewDeviations = async filter => {
  try {
    const { data } = await apolloProvider.clients.defaultClient.query({
      query: gql`
        query GetUnhandledCrewDeviations(
          $filter: UnhandledCrewDeviationFilterInput!
        ) {
          unhandledCrewDeviations(filter: $filter) {
            id
            duty_date
            train_number
            from
            to
            crew_deviation {
              uuid
              event {
                uuid
                title
              }
            }
          }
        }
      `,
      fetchPolicy: 'no-cache',
      variables: {
        filter
      }
    })

    return data.unhandledCrewDeviations
  } catch (err) {
    console.log(err)
    return []
  }
}

export const queryUnhandledTrainDeviations = async filter => {
  try {
    const { data } = await apolloProvider.clients.defaultClient.query({
      query: gql`
        query unhandledTrainDeviations(
          $filter: UnhandledTrainDeviationsInput!
        ) {
          unhandledTrainDeviations(filter: $filter) {
            type
            deviation_type
            train_number
            technical_number
            from
            to
            departure
            arrival
            trainDeviation {
              uuid
              event_uuid
              event {
                title
              }
            }
          }
        }
      `,
      fetchPolicy: 'no-cache',
      variables: {
        filter
      }
    })
    return data.unhandledTrainDeviations
  } catch (err) {
    console.log(err)
    return []
  }
}

const actions = {
  async fetchUnhandledDelays({ commit }, payload) {
    const filter = {
      ...period,
      ...payload
    }
    commit('SET_UNHANDLED_DELAYS', await queryUnhandledDelays(filter))
  },

  async fetchUnhandledCrewDeviations({ commit }, payload) {
    const filter = {
      ...period,
      ...payload
    }
    commit(
      'SET_UNHANDLED_CREW_DEVIATIONS',
      await queryUnhandledCrewDeviations(filter)
    )
  },

  async fetchUnhandledTrainDeviations({ commit }, payload) {
    const filter = {
      ...period,
      ...payload
    }
    commit(
      'SET_UNHANDLED_TRAIN_DEVIATIONS',
      await queryUnhandledTrainDeviations(filter)
    )
  },

  async fetchStatelessUnhandledCrewDeviations(_, payload) {
    const filter = {
      ...period,
      ...payload
    }
    return await queryUnhandledCrewDeviations(filter)
  },

  async fetchStatelessUnhandledTrainDeviations(_, payload) {
    const filter = {
      ...period,
      ...payload
    }
    return await queryUnhandledTrainDeviations(filter)
  },

  async fetchStatelessUnhandledDelays(_, payload) {
    const filter = {
      ...period,
      ...payload
    }
    return await queryUnhandledDelays(filter)
  }
}

const getters = {
  countUnhandled(_, getters) {
    return getters['countUnhandledDelays']
  },

  countTotalUnhandled(_, getters) {
    return (
      getters['countUnhandledDelays'] +
      getters['countUnhandledTrainDeviations'] +
      getters['countUnhandledCrewDeviations']
    )
  },

  countUnhandledDelays(state) {
    return state.unhandledDelays.filter(x => !x.delay).length
  },

  countUnhandledCrewDeviations(state, getters, rootState) {
    if (rootState.app.selectedDistrictId !== 5) return 0
    return state.unhandledCrewDeviations.filter(x => !x.crew_deviation).length
  },

  countUnhandledTrainDeviations(state) {
    return state.unhandledTrainDeviations.filter(x => !x.trainDeviation).length
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
